import {
    IAccount,
    IGood,
    IOrderMaterial,
    IOrderMaterialCreateDto,
    IOrderMaterialGetDto,
    IOrderMaterialPaymentDto,
    IOrderMaterialUpdateDto,
    IOrderWork,
    IStore,
    Locale,
} from "@lib";
import { Modal } from "@core/components/alt-ui/modal";
import {
    Button,
    CheckBox,
    CheckBoxValueChangedEventArgs,
    ComboBoxChangedEventArgs,
    Control,
    IconPackage,
    Panel,
    Select,
    TextArea,
    TextBox,
} from "@core/components/alt-ui/controls";
import { WarrantyBox } from "@/@core/controls/warranty-box";
import { Localizer } from "@/i18n/localizer";
import { DelayedOperation } from "@/utils/delayed-operation";
import { OrderMaterialModalSettings } from "@/utils/plugins/settings.plugin";

type OrderWorkRef = { text: string; ref?: IOrderWork };

export interface IMaterialModal2Context {
    material?: IOrderMaterial;
    accounts: IAccount[];
    stores: IStore[];
    works: IOrderWork[];
    readonly: boolean;
    settings?: OrderMaterialModalSettings;
    searchGoods?: (search: string) => Promise<IGood[]>;
}

export class MaterialModal2 extends Modal<IMaterialModal2Context> {
    private cbGoodSearch!: Select<IGood>;
    private tbSku!: TextBox;
    private tbName!: TextBox;
    private taDescription!: TextArea;
    private tbCost!: TextBox;
    private tbPrice!: TextBox;
    private tbQuantity!: TextBox;
    private wbWarranty!: WarrantyBox;
    private cbWork!: Select<OrderWorkRef>;

    private chbEnableAccountWithdrawal!: CheckBox;
    private cbAccount!: Select<IAccount>;
    private chbEnableStoreAddition!: CheckBox;
    private cbStore!: Select<IStore>;
    private pnlCreateOnlySettings!: Panel;

    private btnCancel!: Button;
    private btnSave!: Button;
    private pnlFooter!: Panel;

    private context: IMaterialModal2Context | null = null;

    public onGet: ((dto: IOrderMaterialGetDto) => Promise<boolean>) | null = null;
    public onCreate: ((dto: IOrderMaterialCreateDto) => Promise<boolean>) | null = null;
    public onUpdate: ((orig: IOrderMaterial, dto: IOrderMaterialUpdateDto) => Promise<boolean>) | null = null;

    public constructor() {
        super("material-modal2", "");
        this.initializeControls();
    }

    public show(context: IMaterialModal2Context): Promise<void> {
        this.context = context;
        this.initializeControls();

        if (context.material) {
            this.title = "Изменение материала";
            this.btnSave.text = "Изменить";
            this.populateControls(context.material);
        } else {
            this.title = "Новый материал";
            this.btnSave.text = "Добавить";
        }

        return super.show();
    }

    protected initializeControls(): void {
        this.cbGoodSearch = new Select<IGood>();
        this.cbGoodSearch.id = "material2.good";
        this.cbGoodSearch.label = "Искать материал";
        this.cbGoodSearch.items = [];
        this.cbGoodSearch.iconPackage = IconPackage.Feater;
        this.cbGoodSearch.icon = "SearchIcon";
        this.cbGoodSearch.selectedIndex = this.cbGoodSearch.items.length > 0 ? 0 : -1;
        this.cbGoodSearch.clearable = true;
        this.cbGoodSearch.textField = s => s.info.name;
        this.cbGoodSearch.descriptionField = this.goodDescriptionField.bind(this);
        this.cbGoodSearch.search = this.searchMaterial.bind(this);
        this.cbGoodSearch.addChangedHandler(this.selectMaterial.bind(this));

        this.tbSku = new TextBox();
        this.tbSku.id = "material2.sku";
        this.tbSku.label = "Артикул";

        this.tbName = new TextBox();
        this.tbName.id = "material2.name";
        this.tbName.label = "Наименование";
        this.tbName.validation = "required";

        this.taDescription = new TextArea();
        this.taDescription.id = "material2.description";
        this.taDescription.label = "Описание";

        this.tbCost = new TextBox();
        this.tbCost.id = "material2.cost";
        this.tbCost.label = "Себестоимость";
        this.tbCost.validation = "required|money|unsigned";

        this.tbPrice = new TextBox();
        this.tbPrice.id = "material2.price";
        this.tbPrice.label = "Цена продажи";
        this.tbPrice.validation = "required|money|unsigned";

        this.tbQuantity = new TextBox();
        this.tbQuantity.id = "material2.quantity";
        this.tbQuantity.label = "Количество";
        this.tbQuantity.validation = `required|numeric|unsigned|min_value:1|max_value:${Number.MAX_VALUE}`;
        this.tbQuantity.text = "1";

        this.wbWarranty = new WarrantyBox();
        this.wbWarranty.id = "material2.warranty";
        this.wbWarranty.label = "Гарантия";

        const works: OrderWorkRef[] = this.context?.works.map(w => ({ text: w.name, ref: w })) ?? [];
        works.unshift({ text: "-- Не привязано --", ref: undefined });

        this.cbWork = new Select<OrderWorkRef>();
        this.cbWork.id = "material2.work";
        this.cbWork.label = "Привязать к работе";
        this.cbWork.items = works;
        this.cbWork.textField = w => w.text;
        this.cbWork.iconPackage = IconPackage.Feater;
        this.cbWork.icon = "ToolIcon";
        this.cbWork.selectedIndex = 0;

        //

        const selectedAccountId = this.context?.settings?.accountId;
        const selectedAccountIndex = this.context?.accounts?.findIndex(a => a.id === selectedAccountId) ?? -1;
        const selectedAccountIndexDefault = (this.context?.accounts ?? []).length > 0 ? 0 : -1;

        this.cbAccount = new Select<IAccount>();
        this.cbAccount.id = "material2.account";
        this.cbAccount.label = "Счёт";
        this.cbAccount.items = this.context?.accounts ?? [];
        this.cbAccount.textField = a => a.info.name;
        this.cbAccount.descriptionField = a => a.info.description;
        this.cbAccount.iconPackage = IconPackage.Alt;
        this.cbAccount.icon = "finance";
        this.cbAccount.selectedIndex = selectedAccountIndex >= 0 ? selectedAccountIndex : selectedAccountIndexDefault;
        this.cbAccount.class = "mb-1";

        const accountWidthdrawalChecked =
            this.context?.settings?.accountWidthdrawal !== undefined ? this.context.settings.accountWidthdrawal : true;

        this.chbEnableAccountWithdrawal = new CheckBox();
        this.chbEnableAccountWithdrawal.id = "material2.enable-account-withdrawal";
        this.chbEnableAccountWithdrawal.text = "Вычесть со счёта";
        this.chbEnableAccountWithdrawal.addValueChangedHandler(this.changeEnableAccountWithdrawal.bind(this));
        this.chbEnableAccountWithdrawal.value = accountWidthdrawalChecked;

        const storeAdditionChecked =
            this.context?.settings?.storeAddition !== undefined ? this.context.settings.storeAddition : false;

        const selectedStoreId = this.context?.settings?.storeId;
        const selectedStoreIndex = this.context?.stores?.findIndex(a => a.id === selectedStoreId) ?? -1;
        const selectedStoreIndexDefault = (this.context?.stores ?? []).length > 0 ? 0 : -1;

        this.cbStore = new Select<IStore>();
        this.cbStore.id = "material2.store";
        this.cbStore.label = "Склад";
        this.cbStore.items = this.context?.stores ?? [];
        this.cbStore.textField = s => s.info.name;
        this.cbStore.descriptionField = s => s.info.description;
        this.cbStore.iconPackage = IconPackage.Feater;
        this.cbStore.icon = "PackageIcon";
        this.cbStore.selectedIndex = selectedStoreIndex >= 0 ? selectedStoreIndex : selectedStoreIndexDefault;
        this.cbStore.visible = storeAdditionChecked;

        this.chbEnableStoreAddition = new CheckBox();
        this.chbEnableStoreAddition.id = "material2.enable-store-addition";
        this.chbEnableStoreAddition.text = "Добавить на склад";
        this.chbEnableStoreAddition.addValueChangedHandler(this.changeEnableStoreAddition.bind(this));
        this.chbEnableStoreAddition.value = storeAdditionChecked;

        this.pnlCreateOnlySettings = new Panel();
        this.pnlCreateOnlySettings.addControls([
            this.chbEnableAccountWithdrawal,
            this.cbAccount,
            this.chbEnableStoreAddition,
            this.cbStore,
        ]);

        //

        this.btnCancel = new Button();
        this.btnCancel.id = "material2.cancel";
        this.btnCancel.text = "Отменить";
        this.btnCancel.variant = "outline-danger";
        this.btnCancel.addClickHandler(this.clickCancel.bind(this));

        this.btnSave = new Button();
        this.btnSave.id = "material2.save";
        this.btnSave.text = "Добавить";
        this.btnSave.class = "ml-0.75";
        this.btnSave.addClickHandler(this.clickSave.bind(this));

        this.pnlFooter = new Panel();
        this.pnlFooter.class = "flex justify-end mt-2";
        this.pnlFooter.addControls([this.btnCancel, this.btnSave]);
    }

    public get controls(): Control[] {
        return [
            this.cbGoodSearch,
            this.tbSku,
            this.tbName,
            this.taDescription,
            this.tbCost,
            this.tbPrice,
            this.tbQuantity,
            this.wbWarranty,
            this.cbWork,
            this.pnlCreateOnlySettings,
            this.pnlFooter,
        ] as Control[];
    }

    private populateControls(material: IOrderMaterial): void {
        const locale = material.storeRef?.info.locale ?? Locale.RU;

        this.tbSku.text = material.sku ?? "";
        this.tbName.text = material.name;
        this.taDescription.text = material.description ?? "";
        this.tbCost.text = Localizer.number(material.cost, locale);
        this.tbPrice.text = Localizer.number(material.price, locale);
        this.tbQuantity.text = material.quantity.toString();
        this.wbWarranty.value = material.warranty;

        if (material.work) {
            const index = this.cbWork.items.findIndex(i => i.ref?.id === material.work);
            this.cbWork.selectedIndex = index;
        }

        this.pnlCreateOnlySettings.visible = false;
        this.cbGoodSearch.visible = false;

        if (this.context?.readonly) {
            this.tbSku.disabled = true;
            this.tbName.disabled = true;
            this.taDescription.disabled = true;
            this.tbCost.disabled = true;
            this.tbPrice.disabled = true;
            this.tbQuantity.disabled = true;
            this.wbWarranty.disabled = true;
            this.cbWork.disabled = true;
            this.chbEnableAccountWithdrawal.disabled = true;
            this.cbAccount.disabled = true;
            this.chbEnableStoreAddition.disabled = true;
            this.cbStore.disabled = true;
            this.btnSave.visible = false;
        }
    }

    private searchMaterial(search: string): void {
        if (!this.context || !this.context.searchGoods) {
            return;
        }

        const searchGoods = this.context.searchGoods;
        search = search.trim();

        // начинать поиск от 2 символов
        if (search.length < 2) {
            this.cbGoodSearch.items = [];
            return;
        }

        DelayedOperation.invoke("search-material", 700, async () => {
            this.cbGoodSearch.items = await searchGoods(search);
        });
    }

    private async selectMaterial(sender: any, e: ComboBoxChangedEventArgs<IGood>): Promise<void> {
        if (!e.item) {
            this.tbCost.text = "";
            this.tbCost.disabled = false;
            this.tbPrice.text = "";
            this.tbQuantity.label = "Количество";
            this.tbQuantity.validation = `required|numeric|unsigned|min_value:1|max_value:${Number.MAX_VALUE}`;
            this.tbQuantity.text = "1";
            this.wbWarranty.value = undefined;
            this.cbWork.selectedIndex = -1;

            this.tbSku.visible = true;
            this.tbName.visible = true;
            this.taDescription.visible = true;
            this.pnlCreateOnlySettings.visible = true;
            return;
        }

        this.tbSku.visible = false;
        this.tbName.visible = false;
        this.taDescription.visible = false;
        this.pnlCreateOnlySettings.visible = false;

        const material = e.item;
        const store = this.getGoodStore(material);
        const locale = store?.info?.locale ?? Locale.RU;
        const cost = material.info.cost ?? 0.0;
        const price = material.info.price ?? 0.0;
        const quantity = material.info.quantity ?? 0;

        this.tbCost.disabled = true;
        this.tbCost.text = Localizer.money(cost, locale);
        this.tbPrice.text = Localizer.money(price, locale);
        this.tbQuantity.label = `Количество (макс: ${quantity})`;
        this.tbQuantity.validation = `required|numeric|unsigned|min_value:1|max_value:${quantity}`;
        this.tbQuantity.text = "1";
        this.wbWarranty.value = material.info.warranty;
    }

    private goodDescriptionField(good: IGood): string {
        const store = this.getGoodStore(good);
        if (!store) {
            return `${good.info.quantity} шт.`;
        }

        return `${store.info.name} (${good.info.quantity} шт.)`;
    }

    private getGoodStore(good: IGood): IStore | undefined {
        return typeof good.store === "string" ? this.context?.stores.find(s => s.id === good.store) : good.store;
    }

    private async changeEnableAccountWithdrawal(sender: any, e: CheckBoxValueChangedEventArgs): Promise<void> {
        this.cbAccount.visible = e.value;
    }

    private async changeEnableStoreAddition(sender: any, e: CheckBoxValueChangedEventArgs): Promise<void> {
        this.cbStore.visible = e.value;
    }

    private getAccountPayment(): IOrderMaterialPaymentDto | undefined {
        if (this.chbEnableAccountWithdrawal.value && this.cbAccount.selectedItem) {
            return {
                account: this.cbAccount.selectedItem.id,
                description: `Покупка материалов (${this.tbQuantity.text} шт.)`,
            };
        }
    }

    private getAdditionStoreId(): string | undefined {
        if (this.chbEnableStoreAddition.value && this.cbStore.selectedItem) {
            return this.cbStore.selectedItem.id;
        }
    }

    private async clickCancel(sender: any, e: any): Promise<void> {
        this.hide();
    }

    private async clickSave(sender: any, e: any): Promise<void> {
        const valid = await this.validate();
        if (!valid) {
            return;
        }

        const result = await this.getSaveResult();
        if (result) {
            this.hide(result);
        }
    }

    private async getSaveResult(): Promise<boolean> {
        if (this.context?.material) {
            return await this.updateMaterial(this.context.material);
        }

        if (this.cbGoodSearch.selectedItem) {
            return await this.getMaterial();
        }

        return await this.createMaterial();
    }

    private async getMaterial(): Promise<boolean> {
        if (!this.onGet || !this.cbGoodSearch.selectedItem) {
            return false;
        }

        const priceStr = this.tbPrice.text.replace(",", ".");

        const store = this.getGoodStore(this.cbGoodSearch.selectedItem);
        if (!store) {
            return false;
        }

        const dto: IOrderMaterialGetDto = {
            price: parseFloat(priceStr),
            quantity: parseInt(this.tbQuantity.text),
            warranty: this.wbWarranty.value,
            work: this.cbWork.selectedItem?.ref?.id,
            store: store.id,
            good: this.cbGoodSearch.selectedItem.id,
        };

        return await this.onGet(dto);
    }

    private async createMaterial(): Promise<boolean> {
        if (!this.onCreate) {
            return false;
        }

        const costStr = this.tbCost.text.replace(",", ".");
        const priceStr = this.tbPrice.text.replace(",", ".");

        const dto: IOrderMaterialCreateDto = {
            sku: this.tbSku.text,
            name: this.tbName.text,
            description: this.taDescription.text,
            cost: parseFloat(costStr),
            price: parseFloat(priceStr),
            quantity: parseInt(this.tbQuantity.text),
            warranty: this.wbWarranty.value,
            work: this.cbWork.selectedItem?.ref?.id,
            store: this.getAdditionStoreId(),
            payment: this.getAccountPayment(),
        };

        return await this.onCreate(dto);
    }

    private async updateMaterial(orig: IOrderMaterial): Promise<boolean> {
        if (!this.onUpdate) {
            return false;
        }

        const costStr = this.tbCost.text.replace(",", ".");
        const priceStr = this.tbPrice.text.replace(",", ".");

        const dto: IOrderMaterialUpdateDto = {
            sku: this.tbSku.text,
            name: this.tbName.text,
            description: this.taDescription.text,
            cost: parseFloat(costStr),
            price: parseFloat(priceStr),
            quantity: parseInt(this.tbQuantity.text),
            warranty: this.wbWarranty.value,
            work: this.cbWork.selectedItem?.ref?.id,
        };

        return await this.onUpdate(orig, dto);
    }
}
